import React, { useState, useRef, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Grid, Typography, useMediaQuery } from '@material-ui/core';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import TextInput from '../../../Components/Inputs/TextInput';
import TurnstileManagedCaptcha from '../../../Components/Captcha/TurnstileManagedCaptcha';
import BaseButton from '../../../Components/Buttons/BaseButton';
import SimpleDivider from '../../../Components/Dividers/SimpleDivider';
import QuestionMark from '../../../Components/Adornments/QuestionMark';
import InvoiceHelpDialog from '../../../Components/Dialogs/InvoiceHelpDialog/InvoiceHelpDialog';
import { defaultAlertError } from '../../../Components/Alerts/AlertMessages';
import NewPaymentCard from '../../../Components/Cards/NewPaymentCard/NewPaymentCard';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { HELP_TEXT } from '../../../Utils/enums';
import { TYPE_OF_PAYMENTS } from '../payment_enums';
import { Company } from '../../../Configs/general';
import { GetCouponByContractAPI } from '../../../API/Coupons/CouponsAPI';
import { useCaptcha } from '../../../Hooks/useCaptcha';

const CAPTCHA_HEIGHT_SCALE = 0.9;

const referenceSchema = yup.object({
  contractId: yup
    .string()
    .trim()
    .required('Ingrese el número de contrato')
});

const ContractCoupon = () => {
  const setAlert = useContext(AlertsDispatchContext);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [coupon, setCoupon] = useState({ details: null, type: 'coupon' });

  const TYPE = TYPE_OF_PAYMENTS.contract;

  const inputRef = useRef(null);
  const { captchaRef, captchaToken, handlers } = useCaptcha();

  const classes = useStyles();
  const isWebSize = useMediaQuery(theme => theme.breakpoints.up('sm'));

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(referenceSchema),
    mode: 'onChange',
    defaultValues: { contractId: '' }
  });

  const canSubmit = !loading && captchaToken && isValid;

  const onHelpClick = useCallback(() => {
    setHelpDialogOpen(true);
  }, []);

  const onSubmit = async data => {
    if (!canSubmit) {
      return;
    }

    setLoading(true);

    const captchaValue = captchaToken;
    const { contractId } = data;

    try {
      const {
        success,
        data: responseData,
        error
      } = await GetCouponByContractAPI(contractId, captchaValue);

      if (!success) {
        setCoupon(prev => ({ ...prev, details: null }));
        defaultAlertError(error, setAlert);
        return;
      }

      setCoupon(prev => ({
        ...prev,
        details: {
          contractCity: responseData.city,
          contractAddress: responseData.address,
          value: responseData.value,
          couponId: responseData.number,
          contractId
        }
      }));
      reset({ contractId: '' });
    } catch (error) {
      setCoupon(prev => ({ ...prev, details: null }));
      defaultAlertError(error, setAlert);
    } finally {
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
      setLoading(false);
    }
  };

  return (
    <>
      <InvoiceHelpDialog
        open={helpDialogOpen}
        type={TYPE}
        onClose={() => setHelpDialogOpen(false)}
        title={HELP_TEXT[TYPE]}
      />
      <Container maxWidth="md" className={classes.root}>
        <Typography className={classes.title}>
          Último cupón disponible para pago
        </Typography>
        <Grid container spacing={3} className={classes.formContainer}>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="contractId"
              inputRef={register}
              control={control}
              name="contractId"
              margin="none"
              autoFocus
              disabled={loading}
              error={Boolean(errors.contractId)}
              label={`Número de ${Company.contractConjugation.regular.singular.main}`}
              InputProps={{
                endAdornment: <QuestionMark onClick={onHelpClick} />,
                inputProps: {
                  type: 'number',
                  inputMode: 'numeric'
                }
              }}
              required={true}
              helperText={
                errors && errors.contractId
                  ? errors.contractId.message || errors.contractId.types.message
                  : ''
              }
              fullWidth
            />
          </Grid>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <div ref={inputRef} style={{ width: '100%' }}>
              <TurnstileManagedCaptcha
                captchaRef={captchaRef}
                referenceEl={inputRef}
                locale="es"
                heightScale={CAPTCHA_HEIGHT_SCALE}
                style={{ marginTop: 0 }}
                onResolved={handlers.onResolved}
                onExpired={handlers.onExpired}
                onLoaded={handlers.onLoaded}
                onError={handlers.onError}
              />
            </div>
          </Grid>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <BaseButton
              id="QueryPayment_button_submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!canSubmit}
              fullWidth
              color="primary"
              variant="outlined"
              height={58}
            >
              Consultar
            </BaseButton>
          </Grid>
        </Grid>
        <SimpleDivider withoutMargin />
        <NewPaymentCard
          details={coupon.details}
          type={coupon.type}
          downloadCouponOption={false}
        />
        {coupon.details && (
          <Grid item xs={12} className={classes.consultContainer}>
            <BaseButton
              id="Payment_button"
              onClick={() =>
                history.push(
                  `${ROUTE_NAMES.payCoupon}/${coupon.details.couponId}`
                )
              }
              fullWidth={!isWebSize}
              color="primary"
              variant="contained"
            >
              Pagar cupón
            </BaseButton>
          </Grid>
        )}
      </Container>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-around',
    padding: theme.spacing(0, 3),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(3)
    }
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.2,
    justifyContent: 'center',
    marginBottom: theme.spacing(3)
  },
  consultContainer: {
    textAlign: 'end',
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      paddingBottom: '0 !important'
    },
    padding: theme.spacing(2, 0)
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      marginTop: theme.spacing(3),
      paddingTop: '0 !important',
      paddingBottom: '0 !important'
    }
  },
  formContainer: {
    marginBottom: theme.spacing(3)
  }
}));

export default ContractCoupon;
